/**
 * useClinicalGuard - Client-side clinical safety checks
 * Detects drug interactions, allergy conflicts, contraindications, and dosage issues
 * Adapted from Alta's useClinicalGuard.ts
 */

import { useMemo } from 'react';
import type { FHIRItem, AISuggestion, SOAPData } from '../services/medicalAI';
import type { PatientContext } from '../services/medicalAI';

export type AlertLevel = 'high' | 'medium' | 'low';

export interface ClinicalAlert {
  id: string;
  level: AlertLevel;
  message: string;
  category: 'allergy' | 'interaction' | 'contraindication' | 'dosage' | 'missing';
}

// Drug classes for cross-reactivity detection
const DRUG_CLASSES: Record<string, string[]> = {
  'penicilina': ['amoxicilina', 'ampicilina', 'penicilina', 'augmentin', 'amoxicilina-clavulánico', 'cloxacilina', 'piperacilina'],
  'sulfonamidas': ['trimetoprim-sulfametoxazol', 'sulfasalazina', 'sulfadiazina', 'sulfonamida'],
  'aines': ['aspirina', 'ibuprofeno', 'naproxeno', 'diclofenaco', 'ketorolaco', 'meloxicam', 'celecoxib', 'indometacina', 'piroxicam'],
  'cefalosporinas': ['cefalexina', 'cefadroxilo', 'cefuroxima', 'ceftriaxona', 'cefotaxima'],
};

// Drug interactions database
const INTERACTIONS: { drug: string; interactsWith: string[]; message: string; level: AlertLevel }[] = [
  { drug: 'warfarina', interactsWith: ['aspirina', 'ibuprofeno', 'naproxeno', 'diclofenaco', 'ketorolaco'], message: '⚠️ SANGRADO: Riesgo aumentado de hemorragia (Anticoagulante + AINE)', level: 'high' },
  { drug: 'rivaroxabán', interactsWith: ['aspirina', 'ibuprofeno', 'naproxeno', 'diclofenaco'], message: '⚠️ SANGRADO: Riesgo de hemorragia (Anticoagulante + AINE)', level: 'high' },
  { drug: 'apixabán', interactsWith: ['aspirina', 'ibuprofeno', 'naproxeno', 'diclofenaco'], message: '⚠️ SANGRADO: Riesgo de hemorragia (Anticoagulante + AINE)', level: 'high' },
  { drug: 'sildenafil', interactsWith: ['nitroglicerina', 'isosorbide'], message: '🚨 HIPOTENSIÓN SEVERA: Contraindicación absoluta (Nitrato + Inhibidor PDE5)', level: 'high' },
  { drug: 'enalapril', interactsWith: ['espironolactona', 'amilorida'], message: '⚠️ HIPERPOTASEMIA: Monitorizar K+ sérico (IECA + Diurético ahorrador K+)', level: 'medium' },
  { drug: 'lisinopril', interactsWith: ['espironolactona', 'amilorida'], message: '⚠️ HIPERPOTASEMIA: Monitorizar K+ sérico (IECA + Diurético ahorrador K+)', level: 'medium' },
  { drug: 'losartán', interactsWith: ['espironolactona', 'amilorida'], message: '⚠️ HIPERPOTASEMIA: Monitorizar K+ sérico (ARA-II + Diurético ahorrador K+)', level: 'medium' },
  { drug: 'propranolol', interactsWith: ['insulina', 'insulina glargina', 'insulina lispro'], message: '⚠️ HIPOGLUCEMIA: Enmascara síntomas (Beta bloqueador + Insulina)', level: 'medium' },
  { drug: 'metoprolol', interactsWith: ['insulina', 'insulina glargina'], message: '⚠️ HIPOGLUCEMIA: Enmascara síntomas (Beta bloqueador + Insulina)', level: 'medium' },
  { drug: 'alprazolam', interactsWith: ['tramadol', 'morfina', 'codeina', 'fentanilo', 'oxicodona'], message: '🚨 DEPRESIÓN RESPIRATORIA: Evitar combinación (Benzodiacepina + Opioide)', level: 'high' },
  { drug: 'lorazepam', interactsWith: ['tramadol', 'morfina', 'codeina', 'fentanilo'], message: '🚨 DEPRESIÓN RESPIRATORIA: Evitar combinación (Benzodiacepina + Opioide)', level: 'high' },
  { drug: 'diazepam', interactsWith: ['tramadol', 'morfina', 'codeina', 'fentanilo'], message: '🚨 DEPRESIÓN RESPIRATORIA: Evitar combinación (Benzodiacepina + Opioide)', level: 'high' },
  { drug: 'clonazepam', interactsWith: ['tramadol', 'morfina', 'codeina', 'fentanilo'], message: '🚨 DEPRESIÓN RESPIRATORIA: Evitar combinación (Benzodiacepina + Opioide)', level: 'high' },
  { drug: 'atorvastatina', interactsWith: ['claritromicina', 'eritromicina'], message: '⚠️ MIOPATÍA: Riesgo de rabdomiólisis (Estatina + Macrólido)', level: 'medium' },
  { drug: 'simvastatina', interactsWith: ['claritromicina', 'eritromicina'], message: '⚠️ MIOPATÍA: Riesgo de rabdomiólisis (Estatina + Macrólido)', level: 'medium' },
  { drug: 'metformina', interactsWith: ['alcohol', 'medio de contraste'], message: '⚠️ ACIDOSIS LÁCTICA: Riesgo aumentado con Metformina', level: 'medium' },
];

// Contraindications: drug vs condition
const CONTRAINDICATIONS: { drug: string; conditions: string[]; message: string }[] = [
  { drug: 'ibuprofeno', conditions: ['insuficiencia renal', 'úlcera péptica', 'úlcera gástrica', 'gastritis'], message: '🚨 CONTRAINDICACIÓN: Ibuprofeno contraindicado en {condition}' },
  { drug: 'naproxeno', conditions: ['insuficiencia renal', 'úlcera péptica', 'úlcera gástrica'], message: '🚨 CONTRAINDICACIÓN: Naproxeno contraindicado en {condition}' },
  { drug: 'diclofenaco', conditions: ['insuficiencia renal', 'úlcera péptica', 'insuficiencia cardíaca'], message: '🚨 CONTRAINDICACIÓN: Diclofenaco contraindicado en {condition}' },
  { drug: 'metformina', conditions: ['insuficiencia renal', 'insuficiencia hepática'], message: '🚨 CONTRAINDICACIÓN: Metformina contraindicada en {condition}' },
  { drug: 'enalapril', conditions: ['embarazo', 'angioedema'], message: '🚨 CONTRAINDICACIÓN: Enalapril contraindicado en {condition}' },
  { drug: 'lisinopril', conditions: ['embarazo', 'angioedema'], message: '🚨 CONTRAINDICACIÓN: Lisinopril contraindicado en {condition}' },
  { drug: 'losartán', conditions: ['embarazo'], message: '🚨 CONTRAINDICACIÓN: Losartán contraindicado en {condition}' },
  { drug: 'warfarina', conditions: ['embarazo', 'úlcera péptica activa', 'hemorragia activa'], message: '🚨 CONTRAINDICACIÓN: Warfarina contraindicada en {condition}' },
];

// Max daily doses
const MAX_DOSES: Record<string, { max: number; unit: string }> = {
  'paracetamol': { max: 4000, unit: 'mg' },
  'acetaminofén': { max: 4000, unit: 'mg' },
  'ibuprofeno': { max: 3200, unit: 'mg' },
  'naproxeno': { max: 1500, unit: 'mg' },
  'diclofenaco': { max: 150, unit: 'mg' },
  'enalapril': { max: 40, unit: 'mg' },
  'metformina': { max: 2550, unit: 'mg' },
  'atorvastatina': { max: 80, unit: 'mg' },
  'losartán': { max: 100, unit: 'mg' },
};

function normalize(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function drugMatchesAllergen(drug: string, allergen: string): boolean {
  const normDrug = normalize(drug);
  const normAllergen = normalize(allergen);

  // Direct match
  if (normDrug.includes(normAllergen) || normAllergen.includes(normDrug)) return true;

  // Class match
  for (const [className, members] of Object.entries(DRUG_CLASSES)) {
    const normClass = normalize(className);
    if (normAllergen.includes(normClass) || normClass.includes(normAllergen)) {
      if (members.some(m => normDrug.includes(normalize(m)))) return true;
    }
  }
  return false;
}

// Drug classes grouped by therapeutic category (for duplicate detection)
const THERAPEUTIC_CLASSES: Record<string, string[]> = {
  'aines': ['ibuprofeno', 'naproxeno', 'diclofenaco', 'ketorolaco', 'meloxicam', 'celecoxib', 'indometacina', 'piroxicam'],
  'ieca': ['enalapril', 'lisinopril', 'ramipril', 'captopril'],
  'ara-ii': ['losartan', 'valsartan', 'irbesartan', 'telmisartan', 'candesartan'],
  'estatinas': ['atorvastatina', 'simvastatina', 'rosuvastatina', 'pravastatina'],
  'benzodiacepinas': ['alprazolam', 'lorazepam', 'diazepam', 'clonazepam', 'bromazepam'],
  'opioides': ['tramadol', 'morfina', 'codeina', 'fentanilo', 'oxicodona'],
  'ibp': ['omeprazol', 'esomeprazol', 'lansoprazol', 'pantoprazol', 'rabeprazol'],
};

export function useClinicalGuard(patient: PatientContext, fhirItems: FHIRItem[], soap?: SOAPData) {
  const alerts = useMemo(() => {
    const result: ClinicalAlert[] = [];
    const prescribedMeds = fhirItems.filter(f => f.type === 'medication');
    const prescribedConditions = fhirItems.filter(f => f.type === 'condition');

    // All active medication names (patient's existing + newly prescribed)
    const allMedNames = [
      ...patient.medications.map(m => normalize(m)),
      ...prescribedMeds.map(m => normalize(m.display)),
    ];

    // All conditions (patient's existing + newly diagnosed)
    const allConditions = [
      ...patient.conditions.map(c => normalize(c)),
      ...prescribedConditions.map(c => normalize(c.display)),
    ];

    // 1. ALLERGY CHECKS
    for (const med of prescribedMeds) {
      for (const allergy of patient.allergies) {
        if (drugMatchesAllergen(med.display, allergy)) {
          result.push({
            id: `alg-${med.id}`,
            level: 'high',
            message: `🚨 ALERGIA: Paciente alérgico a ${allergy}. ${med.display} puede estar contraindicado.`,
            category: 'allergy',
          });
        }
      }
    }

    // 2. DRUG INTERACTIONS
    for (const interaction of INTERACTIONS) {
      const hasDrug = allMedNames.some(m => m.includes(normalize(interaction.drug)));
      if (hasDrug) {
        for (const interacting of interaction.interactsWith) {
          const normInteracting = normalize(interacting);
          // Check if any OTHER medication matches the interacting drug
          if (allMedNames.some(m => m.includes(normInteracting))) {
            const existsAlready = result.some(r => r.id === `int-${interaction.drug}-${interacting}`);
            if (!existsAlready) {
              result.push({
                id: `int-${interaction.drug}-${interacting}`,
                level: interaction.level,
                message: interaction.message,
                category: 'interaction',
              });
            }
          }
        }
      }
    }

    // 3. CONTRAINDICATIONS
    for (const contra of CONTRAINDICATIONS) {
      const hasDrug = allMedNames.some(m => m.includes(normalize(contra.drug)));
      if (hasDrug) {
        for (const condition of contra.conditions) {
          if (allConditions.some(c => c.includes(normalize(condition)))) {
            result.push({
              id: `contra-${contra.drug}-${condition}`,
              level: 'high',
              message: contra.message.replace('{condition}', condition),
              category: 'contraindication',
            });
          }
        }
      }
    }

    // 4. DOSAGE CHECKS on prescribed medications
    for (const med of prescribedMeds) {
      const normDisplay = normalize(med.display);
      for (const [drugName, limits] of Object.entries(MAX_DOSES)) {
        if (normDisplay.includes(normalize(drugName))) {
          // Try to extract dose from display
          const doseMatch = med.display.match(/(\d+)\s*mg/i);
          if (doseMatch) {
            const dose = parseInt(doseMatch[1], 10);
            if (dose > limits.max) {
              result.push({
                id: `dose-${med.id}`,
                level: 'high',
                message: `⚠️ DOSIS: ${drugName} ${dose}mg excede dosis máxima diaria (${limits.max}${limits.unit})`,
                category: 'dosage',
              });
            }
          }
          // Check for missing dose info
          if (!med.dose && !med.display.match(/\d+\s*(mg|ml|g|mcg)/i)) {
            result.push({
              id: `missing-dose-${med.id}`,
              level: 'low',
              message: `💡 DATO FALTANTE: No se especificó dosis para ${med.display}`,
              category: 'missing',
            });
          }
        }
      }
    }

    return result;
  }, [patient, fhirItems]);

  // ── SUGGESTIONS (non-blocking) ──
  const suggestions = useMemo(() => {
    const result: AISuggestion[] = [];
    const prescribedMeds = fhirItems.filter(f => f.type === 'medication');
    const labOrders = fhirItems.filter(f => f.type === 'labOrder' || f.type === 'labResult');
    const imagingOrders = fhirItems.filter(f => f.type === 'imagingOrder');
    let idCounter = 0;
    const nextId = () => `sug-${++idCounter}`;

    // 1. Missing data on medications (frequency, duration, route)
    for (const med of prescribedMeds) {
      const details = normalize(med.details || '');
      const display = normalize(med.display);
      const combined = `${display} ${details}`;
      if (!combined.match(/cada\s*\d|c\/\d|bid|tid|qd|q\d+h|vez\s*al|veces\s*al|diario/i)) {
        result.push({ id: nextId(), type: 'missing-data', message: `Falta frecuencia para ${med.display}`, section: 'p', priority: 'medium' });
      }
      if (!combined.match(/\d+\s*d[ií]as|\d+\s*semanas|\d+\s*meses|indefinido|continuo|crónico/i)) {
        result.push({ id: nextId(), type: 'missing-data', message: `Falta duración del tratamiento para ${med.display}`, section: 'p', priority: 'low' });
      }
      if (!combined.match(/oral|iv|im|sc|tópic|inhal|sublingual|rectal|v[ií]a/i)) {
        result.push({ id: nextId(), type: 'missing-data', message: `Falta vía de administración para ${med.display}`, section: 'p', priority: 'low' });
      }
    }

    // 2. Missing lab/imaging reason
    for (const lab of labOrders) {
      if (!lab.details || lab.details.trim().length < 3) {
        result.push({ id: nextId(), type: 'missing-data', message: `Falta motivo/indicación para examen: ${lab.display}`, section: 'p', priority: 'low' });
      }
    }
    for (const img of imagingOrders) {
      if (!img.details || img.details.trim().length < 3) {
        result.push({ id: nextId(), type: 'missing-data', message: `Falta región anatómica o indicación para estudio: ${img.display}`, section: 'p', priority: 'low' });
      }
    }

    // 3. Duplicate therapeutic class
    const allMedNames = prescribedMeds.map(m => normalize(m.display));
    for (const [className, members] of Object.entries(THERAPEUTIC_CLASSES)) {
      const found = allMedNames.filter(m => members.some(member => m.includes(normalize(member))));
      if (found.length >= 2) {
        result.push({ id: nextId(), type: 'duplicate', message: `Posible duplicidad terapéutica (${className}): ${found.join(', ')}`, section: 'p', priority: 'medium' });
      }
    }

    // 4. Missing follow-up in plan
    if (soap?.p && soap.p.length > 10) {
      const planNorm = normalize(soap.p);
      if (!planNorm.match(/seguimiento|control|cita|regresar|semana|mes|d[ií]as/)) {
        result.push({ id: nextId(), type: 'incomplete', message: 'Considerar agregar plan de seguimiento o próxima cita', section: 'p', priority: 'low' });
      }
    }

    // 5. Preventive suggestions by age/gender/conditions
    if (patient.age >= 45 && !patient.conditions.some(c => normalize(c).includes('diabetes'))) {
      const hasGlucose = labOrders.some(l => normalize(l.display).match(/glucosa|glicemia|hba1c|hemoglobina glicosilada/));
      if (!hasGlucose) {
        result.push({ id: nextId(), type: 'preventive', message: 'Paciente ≥45 años: considerar tamizaje de glucosa/HbA1c', priority: 'low' });
      }
    }
    if (patient.age >= 50 && patient.gender && normalize(patient.gender).match(/fem|mujer/)) {
      result.push({ id: nextId(), type: 'preventive', message: 'Mujer ≥50 años: verificar mamografía y densitometría ósea actualizadas', priority: 'low' });
    }
    if (patient.age >= 50) {
      result.push({ id: nextId(), type: 'preventive', message: 'Paciente ≥50 años: verificar tamizaje de cáncer colorrectal actualizado', priority: 'low' });
    }

    return result;
  }, [patient, fhirItems, soap]);

  const highCount = alerts.filter(a => a.level === 'high').length;
  const mediumCount = alerts.filter(a => a.level === 'medium').length;

  return { alerts, suggestions, highCount, mediumCount };
}
